import React, { useState } from 'react';
import { Eye, EyeOff, Lock, Mail, Eye as EyeIcon, Sparkles, Shield, Zap, Glasses, Eye as EyeIcon2, Sun, Moon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ModernCard, ModernButton, ModernInput, ModernNotification } from '../components/ui/ModernComponents';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError(''); // Clear error when user starts typing
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await login(formData);
      navigate('/');
    } catch (err) {
      setError(err.message || 'Une erreur est survenue lors de la connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-indigo-900 to-cyan-900">
        {/* Floating Optical Elements */}
        <div className="absolute top-20 left-20 w-72 h-72 bg-blue-500/20 rounded-full blur-3xl animate-pulse">
          <div className="absolute inset-0 flex items-center justify-center">
            <Glasses className="w-16 h-16 text-blue-300/40" />
          </div>
        </div>
        <div className="absolute top-40 right-20 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse delay-1000">
          <div className="absolute inset-0 flex items-center justify-center">
            <EyeIcon2 className="w-20 h-20 text-cyan-300/40" />
          </div>
        </div>
        <div className="absolute bottom-20 left-1/2 w-80 h-80 bg-indigo-500/20 rounded-full blur-3xl animate-pulse delay-2000">
          <div className="absolute inset-0 flex items-center justify-center">
            <Sun className="w-16 h-16 text-indigo-300/40" />
          </div>
        </div>
        
        {/* Optical Grid Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        {/* Floating Glasses Icons */}
        <div className="absolute top-1/4 left-1/4 animate-bounce delay-300">
          <Glasses className="w-8 h-8 text-white/20" />
        </div>
        <div className="absolute top-1/3 right-1/4 animate-bounce delay-700">
          <EyeIcon2 className="w-6 h-6 text-white/20" />
        </div>
        <div className="absolute bottom-1/4 left-1/3 animate-bounce delay-1000">
          <Glasses className="w-10 h-10 text-white/20" />
        </div>
      </div>

      {/* Content Container */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo and Brand Section */}
          <div className="text-center mb-8 animate-fade-in">
            {/* Glowing Logo Container */}
            <div className="relative inline-block mb-6">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-3xl blur-xl opacity-75 animate-pulse"></div>
              <div className="relative bg-white/10 backdrop-blur-xl rounded-3xl p-4 border border-white/20 shadow-2xl">
                <img 
                  src={process.env.PUBLIC_URL + '/logo.jpg'} 
                  alt="EPOPTIQUE Logo" 
                  className="w-20 h-20 object-contain rounded-2xl"
                />
              </div>
            </div>
            
            {/* Brand Text */}
            <h1 className="text-3xl font-bold bg-gradient-to-r from-white to-cyan-200 bg-clip-text text-transparent mb-2">
              EPOPTIQUE
            </h1>
            <p className="text-white/80 text-lg font-medium">Votre Opticien de Confiance</p>
            <p className="text-white/70 text-base">Connectez-vous à votre compte</p>
            
            {/* Feature Icons */}
            <div className="flex items-center justify-center space-x-6 mt-4">
              <div className="flex items-center space-x-2 text-white/70">
                <Shield className="w-4 h-4" />
                <span className="text-sm">Sécurisé</span>
              </div>
              <div className="flex items-center space-x-2 text-white/70">
                <Zap className="w-4 h-4" />
                <span className="text-sm">Rapide</span>
              </div>
              <div className="flex items-center space-x-2 text-white/70">
                <Glasses className="w-4 h-4" />
                <span className="text-sm">Professionnel</span>
              </div>
            </div>
          </div>

          {/* Login Form Card */}
          <div className="animate-slide-in-up">
            <ModernCard className="p-8 shadow-2xl bg-white/95 backdrop-blur-xl border border-white/20">
              {error && (
                <ModernNotification
                  type="danger"
                  message={error}
                  className="mb-6 animate-bounce-in"
                />
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Email Input */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 flex items-center space-x-2">
                    <Mail className="w-4 h-4 text-blue-500" />
                    <span>Adresse e-mail</span>
                  </label>
                  <div className="relative group">
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="votre@email.com"
                      className="w-full px-4 py-3 pl-12 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 bg-white/80 backdrop-blur-sm placeholder-gray-400"
                      required
                    />
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Mail className="w-5 h-5 text-gray-400 group-focus-within:text-blue-500 transition-colors duration-300" />
                    </div>
                  </div>
                </div>

                {/* Password Input */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 flex items-center space-x-2">
                    <Lock className="w-4 h-4 text-blue-500" />
                    <span>Mot de passe</span>
                  </label>
                  <div className="relative group">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Votre mot de passe"
                      className="w-full px-4 py-3 pl-12 pr-12 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 bg-white/80 backdrop-blur-sm placeholder-gray-400"
                      required
                    />
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Lock className="w-5 h-5 text-gray-400 group-focus-within:text-blue-500 transition-colors duration-300" />
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors duration-200"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Remember Me & Forgot Password */}
                <div className="flex items-center justify-center">
                  <label className="flex items-center space-x-2 cursor-pointer group">
                    <div className="relative">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-blue-600 border-2 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200"
                      />
                    </div>
                    <span className="text-sm text-gray-600 group-hover:text-gray-800 transition-colors duration-200">
                      Se souvenir de moi
                    </span>
                  </label>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {loading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Connexion en cours...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center space-x-2">
                      <Glasses className="w-5 h-5" />
                      <span>Se connecter</span>
                    </div>
                  )}
                </button>
              </form>
            </ModernCard>
          </div>

          {/* Footer */}
          <div className="text-center mt-8 animate-fade-in">
            <p className="text-white/60 text-sm">
              © 2025 EPOPTIQUE. Tous droits réservés.
            </p>
            <div className="flex items-center justify-center space-x-4 mt-2">
              <a href="#" className="text-white/60 hover:text-white/80 text-xs transition-colors duration-200 hover:underline">
                Conditions d'utilisation
              </a>
              <span className="text-white/40">•</span>
              <a href="#" className="text-white/60 hover:text-white/80 text-xs transition-colors duration-200 hover:underline">
                Politique de confidentialité
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Action Button */}
      <div className="fixed bottom-6 right-6 z-20">
        <button className="bg-white/20 backdrop-blur-xl border border-white/30 text-white p-3 rounded-full hover:bg-white/30 transition-all duration-300 transform hover:scale-110 shadow-lg">
          <Glasses className="w-6 h-6" />
        </button>
      </div>
    </div>
  );
};

export default Login; 